import {
    BackgroundData, ButtonTheme, ButtonWidgetBaseData,
    IBackgroundStyle,
    IBorderRadiusData,
    IButtonBaseStyle,
    IDigitalProductModel,
    IFontData,
    ImageRatioType,
    IWidgetVisibility, VisibilityData,
    WidgetPlaceType
} from "./index";
import { IAnimatedWidget } from "./widget-style-data";

export interface IPricingTableWidgetData extends IWidgetVisibility {
    type: "PricingTableWidget";
    placeType: WidgetPlaceType;
    id?: string | null;
    style: IPricingTableWidgetStyle;
    pricingPlans: IPricingPlan[];
    highlightedPlanEnabled: boolean;
}

export interface IPricingTableWidgetShortData {
    pricingPlans: IPricingPlanShort[];
    switcherStyle: ISwitcherStyle;
    style: IPricingTableWidgetShortStyle;
    highlightedPlanEnabled: boolean;
}

export interface IPricingPlan {
    productId?: string | null;
    data?: IDigitalProductModel | null;
    theme: PricingPlanTheme;
}

export type PricingPlanTheme = "default" | "highlighted";

export interface IPricingPlanShort {
    productId?: string | null;
    theme: PricingPlanTheme;
}

export interface IPricingTableWidgetStyle extends IAnimatedWidget {
    spaceBetweenItems: number;
    spaceBetweenRows: number;
    showDiscount: boolean;
    font: IFontData;
    switcherStyle: ISwitcherStyle;
    defaultThemeStyle: IPricingPlanBaseStyle;
    highlightedThemeStyle: IPricingPlanHighlightedStyle;
}

export interface IPricingTableWidgetShortStyle {
    spaceBetweenItems: number;
    showDiscount: boolean;
    defaultThemeStyle: IPricingPlanBaseShortStyle;
    highlightedThemeStyle: IPricingPlanHighlightedShortStyle;
}

export interface IPricingPlanBaseStyle {
    padding: number;
    borderRadius: IBorderRadiusData;
    background: IBackgroundStyle;
    borderThickness: number;
    borderColor: string;
    imageRatio?: ImageRatioType | null;
    nameFont?: IFontData | null;
    costFont?: IFontData | null;
    periodFont?: IFontData | null;
    descriptionFont?: IFontData | null;
    mainFeaturesFont?: IFontData | null;
    buttonStyle?: IButtonBaseStyle | null;
    mainFeaturesStyle: IMainFeaturesStyle;
    enabledComponents: IPricingPlanBaseEnabledComponents;
}

export interface IPricingPlanHighlightedStyle extends IPricingPlanBaseStyle {
    ribbonStyle: IRibbonStyle;
    enabledComponents: IPricingPlanHighlightedEnabledComponents;
}

export interface IPricingPlanBaseShortStyle {
    buttonTheme?: ButtonTheme | null;
    imageRatio?: ImageRatioType | null;
    mainFeaturesStyle?: IMainFeaturesStyle | null;
    enabledComponents: IPricingPlanBaseEnabledComponents;
}

export interface IPricingPlanHighlightedShortStyle extends IPricingPlanBaseShortStyle {
    ribbonStyle: IRibbonStyle;
    enabledComponents: IPricingPlanHighlightedEnabledComponents;
}

export interface IRibbonStyle extends IButtonBaseStyle {
    type: RibbonType;
    text: string;
}

export interface ISwitcherStyle {
    type: "rectangle" | "oval";
    backgroundColor?: string | null;
    switchColor?: string | null;
    textColor?: string | null;
    switchTextColor?: string | null;
    font?: IFontData | null;
}

export interface IPricingPlanBaseEnabledComponents {
    imageEnabled: boolean;
    descriptionEnabled: boolean;
    mainFeaturesEnabled: boolean;
}

export interface IPricingPlanHighlightedEnabledComponents extends IPricingPlanBaseEnabledComponents {
    ribbonEnabled: boolean;
}

export interface IMainFeaturesStyle {
    type: MainFeaturesAppearanceType;
}

export type MainFeaturesAppearanceType = "default" | "check" | "circle-check" | "dot";

export type RibbonType = "above" | "stretched" | "inline-left" | "inline-right";

export abstract class PricingTableWidgetData {

    public static get defaultData(): IPricingTableWidgetData {
        return {
            type: "PricingTableWidget",
            placeType: "Section",
            pricingPlans: [],
            style: this.defaultStyle,
            visibility: VisibilityData.defaultData(),
            highlightedPlanEnabled: false
        };
    }

    public static get defaultStyle(): IPricingTableWidgetStyle {
        return {
            spaceBetweenItems: 24,
            spaceBetweenRows: 24,
            font: {
                family: "Open Sans",
            },
            switcherStyle: {
                type: "rectangle",
                font: {
                    weight: "",
                    size: "",
                }
            },
            animation: {
                animationSource: "site-design",
                animationData: {
                    name: "none",
                    duration: 1
                }
            },
            showDiscount: true,
            defaultThemeStyle: this.defaultPlanStyle,
            highlightedThemeStyle: this.defaultHighlightedPlanStyle
        };
    }

    public static get defaultPlanStyle(): IPricingPlanBaseStyle {
        return {
            padding: 16,
            borderRadius: {
                topLeftPx: 0,
                topRightPx: 0,
                bottomLeftPx: 0,
                bottomRightPx: 0
            },
            background: BackgroundData.defaultStyle,
            borderThickness: 0,
            borderColor: "#D2D2D5",
            buttonStyle: ButtonWidgetBaseData.defaultButtonStyle,
            nameFont: {
                weight: "600",
                size: "2rem",
                color: "#000000"
            },
            costFont: {
                weight: "600",
                size: "1.5rem",
                color: "#000000"
            },
            imageRatio: "thumbnail",
            periodFont: this.defaultFontData,
            descriptionFont: this.defaultFontData,
            mainFeaturesFont: this.defaultFontData,
            enabledComponents: this.defaultBaseEnabledComponents,
            mainFeaturesStyle: {
                type: "check",
            }
        };
    }

    public static get defaultHighlightedPlanStyle(): IPricingPlanHighlightedStyle {
        return {
            ...this.defaultPlanStyle,
            ribbonStyle: {
                ...ButtonWidgetBaseData.defaultButtonStyle,
                type: "above",
                text: "Best Value",
                font: {
                    weight: "400",
                    size: "1rem"
                }
            },
            enabledComponents: {
                ...this.defaultBaseEnabledComponents,
                ribbonEnabled: true
            }
        };
    }

    public static get defaultBaseEnabledComponents(): IPricingPlanBaseEnabledComponents {
        return {
            imageEnabled: true,
            descriptionEnabled: true,
            mainFeaturesEnabled: true
        };
    }

    public static isLocalScriptRerenderNeeded(leftHandSide?: IPricingTableWidgetData | null, rightHandSide?: IPricingTableWidgetData | null): boolean {
        if (!leftHandSide || !rightHandSide) {
            return true;
        }

        return this.hasBasePropertiesUpdated(leftHandSide, rightHandSide) ||
            this.hasPricingPlansUpdated(leftHandSide, rightHandSide) ||
            this.hasStylePropertiesUpdated(leftHandSide, rightHandSide);
    }

    private static hasBasePropertiesUpdated(leftHandSide: IPricingTableWidgetData, rightHandSide: IPricingTableWidgetData): boolean {
        return leftHandSide.id !== rightHandSide.id ||
            leftHandSide.style.showDiscount !== rightHandSide.style.showDiscount;
    }

    private static hasPricingPlansUpdated(leftHandSide: IPricingTableWidgetData, rightHandSide: IPricingTableWidgetData): boolean {
        const leftPlans = leftHandSide.pricingPlans ?? [];
        const rightPlans = rightHandSide.pricingPlans ?? [];
        if (leftPlans.length !== rightPlans.length) {
            return true;
        }

        const updated = leftPlans.some((plan, index) =>
            plan.productId !== rightPlans[index]?.productId) ||
            rightPlans.some((plan, index) =>
                plan.productId !== leftPlans[index]?.productId);

        const themeUpdated = leftPlans.some((plan, index) =>
            plan.theme !== rightPlans[index]?.theme);

        return updated || themeUpdated;
    }

    private static hasStylePropertiesUpdated(leftHandSide: IPricingTableWidgetData, rightHandSide: IPricingTableWidgetData): boolean {
        const propertiesToCheck = [
            { path: (style: IPricingPlanBaseStyle) => style.imageRatio },
            { path: (style: IPricingPlanBaseStyle) => style.buttonStyle?.theme },
            { path: (style: IPricingPlanBaseStyle) => style.mainFeaturesStyle?.type },
            { path: (style: IPricingPlanBaseStyle) => style.enabledComponents.imageEnabled },
            { path: (style: IPricingPlanBaseStyle) => style.enabledComponents.descriptionEnabled },
            { path: (style: IPricingPlanBaseStyle) => style.enabledComponents.mainFeaturesEnabled },
            { path: (style: IPricingPlanBaseStyle) => (style as IPricingPlanHighlightedStyle).enabledComponents.ribbonEnabled },
            { path: (style: IPricingPlanBaseStyle) => (style as IPricingPlanHighlightedStyle).ribbonStyle?.text },
            { path: (style: IPricingPlanBaseStyle) => (style as IPricingPlanHighlightedStyle).ribbonStyle?.type }
        ];

        return propertiesToCheck.some(property => {
            return property.path(leftHandSide.style.defaultThemeStyle) !== property.path(rightHandSide.style.defaultThemeStyle) ||
                property.path(leftHandSide.style.highlightedThemeStyle) !== property.path(rightHandSide.style.highlightedThemeStyle);
        });
    }

    private static get defaultFontData(): IFontData {
        return {
            weight: "400",
            size: "1rem",
            color: "#000000",
        };
    }

}

